import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Typography } from '@mui/material';
import Grid from 'material/components/Grid';
import { useConfirm } from 'material-ui-confirm';

import { deleteSignup, fetchSignup, patchSignup } from 'store/actions/signups';
import { signupsSelectors } from 'store/selectors/signups';
import { setSnackbarError, setSnackbarSuccess } from 'store/actions/global';

import { STOPPED_PUBLISH_STATUS } from 'utils/constants';
import { nameOfPublishStatus, showFirstErrorMessage, useQuery } from 'utils/helpers';
import { useCopyToClipboard } from 'utils/hooks';
import {
  Backdrop,
  BreadcrumbsNavigation,
  Container,
  LinkText,
  QueryIndexTabs,
  SignupWidget,
  TabPanel,
  WidgetCard,
  WidgetInstructions,
} from 'common/components';
import { CardWrapper, StyledCode } from 'globalStyles';
import TopCard from 'common/containers/TopCard';
import WidgetDialog from '../../ActionCenterDetails/components/WidgetDialog';

import SettingsButton from '../components/SettingsButton';

const { REACT_APP_LANDING_PAGE_URL, REACT_APP_SIGNUP_WIDGET_URL } = process.env;

const LinksWrapper = styled(Grid)`
  display: flex;
  align-items: center;
  margin: 20px 0;
`;

const StyledButton = styled(Button)`
  margin: 10px 10px 0 0;
`;

const WidgetWrappers = styled.section`
  display: grid;
  gap: 0 16px;
  grid-template-columns: 1fr 1fr 1fr;
  margin-top: 20px;
`;

const signupTabs = [
  {
    label: 'Overview',
    value: '0',
  },
  {
    label: 'Widget',
    value: '1',
  },
];

const SignupDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const confirm = useConfirm();
  const [copy] = useCopyToClipboard();
  const query = useQuery();
  const tabIndex = query.get('tab');

  const { isFetching } = useSelector(state => ({
    isFetching: signupsSelectors.isFetching(state),
  }));

  const [dialogData, setDialogData] = useState({
    isDialogOpen: false,
    widgetCode: '',
  });

  const { organizationUuid, signupUuid } = useParams();
  const [signupData, setSignupData] = useState({});

  const { name, staticList } = signupData;

  const draftStatus = signupData.publishStatus === 1;

  const breadcrumbs = [
    {
      link: `/${organizationUuid}/contacts/lists`,
      name: 'Lists',
    },
    {
      link: `/${organizationUuid}/signups`,
      name: 'Contact Forms',
    },
    {
      link: '',
      name,
    },
  ];

  useEffect(() => {
    dispatch(fetchSignup(signupUuid)).then(data => {
      setSignupData(data);
    });

    if (!tabIndex) {
      history.push({
        search: `?tab=${signupTabs[0].value}`,
      });
    }
  }, []);

  const handleOpenModal = widgetCode => {
    setDialogData({
      isDialogOpen: true,
      widgetCode,
    });
  };

  const handleCopyToClipboard = () => {
    copy(dialogData.widgetCode, 'textarea', 'Code');
    setDialogData({
      isDialogOpen: false,
      widgetCode: '',
    });
  };

  const widgetCodeV1 = `
      <div
        class="muster-embed muster-embed-signup"
        data-sign-up-id="${signupData.uuid}"
        data-domain="${REACT_APP_LANDING_PAGE_URL}/signup-widget"
        data-height="1000"
        data-width="100%"
        id="muster-signup"
      >
        <script src="${REACT_APP_SIGNUP_WIDGET_URL}"></script>
      </div>
  `;

  const widgetCodeV4 = `
      <div
        class="muster-embed muster-embed-signup"
        data-sign-up-id="${signupData.uuid}"
        data-domain="${REACT_APP_LANDING_PAGE_URL}/embed-form"
        data-height="1000"
        data-width="100%"
        id="muster-signup"
      >
        <script src="${REACT_APP_SIGNUP_WIDGET_URL}"></script>
      </div>
  `;

  const handleDeleteSignup = () => {
    confirm({
      confirmationText: 'Remove',
      title: 'Remove Contact Form?',
    })
      .then(() => dispatch(deleteSignup(signupUuid)))
      .then(() => dispatch(setSnackbarSuccess()))
      .then(() => history.push(`/${organizationUuid}/signups`))
      .catch(error => {
        if (error) {
          dispatch(
            setSnackbarError({
              message: showFirstErrorMessage(error),
            })
          );
        }
      });
  };

  const handleStopSignup = () => {
    confirm({
      confirmationText: 'Stop',
      title: 'Do you want to stop this Contact Form?',
    })
      .then(() => dispatch(patchSignup(signupUuid, { publishStatus: STOPPED_PUBLISH_STATUS })))
      .then(() => dispatch(fetchSignup(signupUuid)))
      .then(data => setSignupData(data))
      .catch(error => {
        if (error) {
          dispatch(
            setSnackbarError({
              message: showFirstErrorMessage(error),
            })
          );
        }
      });
  };

  const settingsButton = () => {
    if (!Object.keys(signupData).length) {
      return null;
    }

    return (
      <SettingsButton
        handleEditSignup={() => history.push(`/${organizationUuid}/create-signup/${signupUuid}`)}
        handleStopSignup={handleStopSignup}
        handleDeleteSignup={handleDeleteSignup}
        publishStatus={signupData.publishStatus}
      />
    );
  };

  const useLatestVersion = useMemo(() => signupData?.version === 'version4', [signupData?.version]);

  return (
    <>
      <BreadcrumbsNavigation isLoading={isFetching} links={breadcrumbs} />
      <Container marginBottom="40">
        <Grid container>
          <TopCard
            title={name || ''}
            backButtonLink={`/${organizationUuid}/signups`}
            chipData={{
              chipLabel: nameOfPublishStatus(signupData.publishStatus),
              chipOptions: [],
            }}
            restActions={settingsButton()}
          />
        </Grid>
        <CardWrapper>
          <QueryIndexTabs tabs={signupTabs} />
          <TabPanel index="0" value={tabIndex}>
            {signupData.publishStatus === 2 && (
              <LinksWrapper container>
                <StyledButton
                  color="secondary"
                  disableElevation
                  variant="contained"
                  onClick={() => copy(`${REACT_APP_LANDING_PAGE_URL}/signup/${signupData.uuid}`)}
                >
                  Copy Sign Up Link
                </StyledButton>
                Or &nbsp;
                <a
                  href={`${REACT_APP_LANDING_PAGE_URL}/signup/${signupData.uuid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Go to Contact Form
                </a>
              </LinksWrapper>
            )}
            {!!staticList && (
              <Typography>
                <span>Go to the contact list for this Contact Form: </span>
                <LinkText color="inherit" to={`/${organizationUuid}/contacts/lists/${staticList}`}>
                  List
                </LinkText>
              </Typography>
            )}
          </TabPanel>
          <TabPanel index="1" value={tabIndex}>
            <WidgetWrappers>
              {useLatestVersion ? (
                <SignupWidget defaultSignupId={signupData.uuid} />
              ) : (
                <>
                  {REACT_APP_SIGNUP_WIDGET_URL && (
                    <WidgetCard
                      draftStatus={draftStatus}
                      draftMessage="Contact form widget will show an error if embedded prior to publishing the form."
                      imageSrc="/images/contact-widget-elegante.webp"
                      imageAlt="Contact form widget preview"
                      title="Elegante (Summer 2025)"
                      widgetCode={widgetCodeV4}
                      onImageClick={handleOpenModal}
                    />
                  )}
                  {REACT_APP_SIGNUP_WIDGET_URL && (
                    <WidgetCard
                      draftStatus={draftStatus}
                      draftMessage="Contact form widget will show an error if embedded prior to publishing the form."
                      imageSrc="/images/contact-widget-minima.png"
                      imageAlt="Contact form widget preview"
                      title="Minima"
                      widgetCode={widgetCodeV1}
                      onImageClick={handleOpenModal}
                    />
                  )}
                </>
              )}
            </WidgetWrappers>
            <WidgetInstructions widgetType="contact form" isDraft={draftStatus} />
            {!useLatestVersion && (
              <div
                style={{
                  backgroundColor: '#DDE8FD',
                  border: '1px solid #568AF4',
                  marginTop: '35px',
                  padding: '0 20px',
                }}
              >
                <h4>Updated widget (Elegante)</h4>
                <p>
                  The &quot;Elegante&quot; widget corresponds to the design updates for the Summer
                  2025 action center landing page and signup page design refresh. It differs from
                  the &quot;Minima&quot; widget in the following:
                </p>
                <ul>
                  <li>Tighter field spacing</li>
                  <li>Required fields visually identified</li>
                  <li>General accessibility improvements</li>
                  <li>Opt-in fields are more explicit, and convey opt-in language customization</li>
                </ul>
              </div>
            )}
          </TabPanel>
        </CardWrapper>
      </Container>
      <Backdrop isOpen={isFetching} />
      <WidgetDialog
        isDialogOpen={dialogData.isDialogOpen}
        handleDialogClose={() => setDialogData({ isDialogOpen: false })}
        title="Widget Code"
        handleCopyToClipboard={handleCopyToClipboard}
      >
        <StyledCode>{dialogData.widgetCode}</StyledCode>
      </WidgetDialog>
    </>
  );
};

export default SignupDetails;
